import React, {
  createContext,
  type Dispatch,
  type MutableRefObject,
  useContext,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import type {AgentChatAction} from './actions';
import {agentChatReducer} from './reducer';
import {selectActiveThread} from './selectors';
import type {AgentChatState, Thread} from './thread';
import {createDefaultState} from './thread';

// Action dispatch

const AgentChatDispatchContext = createContext<Dispatch<AgentChatAction>>(
  () => {}
);

export function useAgentChatDispatch(): Dispatch<AgentChatAction> {
  return useContext(AgentChatDispatchContext);
}

// Active thread

const ActiveThreadContext = createContext<Thread | undefined>(undefined);

export function useActiveThread(): Thread | undefined {
  return useContext(ActiveThreadContext);
}

// Active thread load state

export interface ActiveThreadLoadState {
  activeThreadId: string | null;
  error: string | null;
  isLoading: boolean;
}

const ActiveThreadLoadStateContext = createContext<ActiveThreadLoadState>({
  activeThreadId: null,
  error: null,
  isLoading: false,
});

export function useActiveThreadLoadState(): ActiveThreadLoadState {
  return useContext(ActiveThreadLoadStateContext);
}

// State ref

/**
 * Holds a ref to the full AgentChatState so the client-side runtime's message
 * getter can read any thread without subscribing to re-renders.
 *
 * TODO: This context exists only for the client-side runtime's message getter.
 * Remove it when migrating to the backend runtime.
 */
const AgentChatStateRefContext = createContext<
  MutableRefObject<AgentChatState>
>({current: createDefaultState()});

export function useAgentChatStateRef(): MutableRefObject<AgentChatState> {
  return useContext(AgentChatStateRefContext);
}

// Provider

export function AgentChatStoreProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [state, dispatch] = useReducer(agentChatReducer, createDefaultState());

  const stateRef = useRef(state);
  stateRef.current = state;

  const activeThread = selectActiveThread(state);
  const loadState: ActiveThreadLoadState = useMemo(
    () => ({
      activeThreadId: state.activeThreadId,
      error: state.activeThreadLoadError,
      isLoading: state.isActiveThreadLoading,
    }),
    [
      state.activeThreadId,
      state.activeThreadLoadError,
      state.isActiveThreadLoading,
    ]
  );

  return (
    <AgentChatDispatchContext.Provider value={dispatch}>
      <AgentChatStateRefContext.Provider value={stateRef}>
        <ActiveThreadLoadStateContext.Provider value={loadState}>
          <ActiveThreadContext.Provider value={activeThread}>
            {children}
          </ActiveThreadContext.Provider>
        </ActiveThreadLoadStateContext.Provider>
      </AgentChatStateRefContext.Provider>
    </AgentChatDispatchContext.Provider>
  );
}
